import { Component, computed, input, output } from '@angular/core';

import { addIcons } from 'ionicons';
import { addOutline, removeOutline } from 'ionicons/icons';

/**
 * Stepper compacto para incrementar/decrementar uma quantidade entre [min, max].
 *
 * Standalone, sem dependências além de Ionicons. Usado tanto no seletor de
 * vagas (HORARIO_COMPARTILHADO) quanto no card de equipamento — mesmo visual,
 * mesma área de toque (44px+), mesma semântica de disabled.
 *
 * O componente é controlado pelo pai (single source of truth via `[value]`).
 * Emite `valueChange` quando o usuário clica em + ou -. Não atualiza
 * internamente — se o pai não atualizar `value`, nada muda.
 */
import { IonIcon } from '@ionic/angular/standalone';

@Component({
  selector: 'lessor-quantity-stepper',
  standalone: true,
  imports: [IonIcon],
  template: `
    <div class="stepper">
      <button
        type="button"
        class="stepper__btn"
        [disabled]="!canDec()"
        [attr.aria-label]="decLabel()"
        (click)="dec()"
      >
        <ion-icon name="remove-outline" aria-hidden="true"></ion-icon>
      </button>
      <span class="stepper__count" [class.stepper__count--zero]="value() === 0">
        {{ value() }}
      </span>
      <button
        type="button"
        class="stepper__btn"
        [disabled]="!canInc()"
        [attr.aria-label]="incLabel()"
        (click)="inc()"
      >
        <ion-icon name="add-outline" aria-hidden="true"></ion-icon>
      </button>
    </div>
  `,
  styles: [
    `
      :host {
        display: inline-block;
      }
      .stepper {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        background: var(--ion-color-light, #f4f5f8);
        border-radius: 999px;
        padding: 4px;
      }
      .stepper__btn {
        width: 36px;
        height: 36px;
        min-width: 36px;
        border-radius: 50%;
        border: none;
        background: #fff;
        color: var(--ion-color-primary, #3880ff);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font: inherit;
        cursor: pointer;
        transition: transform 0.1s ease;
      }
      .stepper__btn:active {
        transform: scale(0.92);
      }
      .stepper__btn:disabled {
        color: var(--ion-color-medium, #c5c6c9);
        cursor: not-allowed;
      }
      .stepper__btn ion-icon {
        font-size: 20px;
      }
      .stepper__count {
        min-width: 28px;
        text-align: center;
        font-weight: 700;
        font-size: 15px;
        color: var(--ion-color-dark, #222);
      }
      .stepper__count--zero {
        color: var(--ion-color-medium, #92949c);
        font-weight: 500;
      }
    `,
  ],
})
export class QuantityStepperComponent {
  readonly value = input.required<number>();
  readonly min = input<number>(0);
  readonly max = input<number>(Number.POSITIVE_INFINITY);
  /** Quando o `-` é clicado em min, ainda emite (deixa o pai decidir, ex: pedir confirmação pra remover). */
  readonly emitOnMin = input<boolean>(false);
  readonly decLabel = input<string>('Diminuir');
  readonly incLabel = input<string>('Aumentar');

  readonly valueChange = output<number>();

  readonly canInc = computed(() => this.value() < this.max());
  readonly canDec = computed(() =>
    this.emitOnMin() ? this.value() > 0 : this.value() > this.min()
  );

  inc(): void {
    if (this.canInc()) this.valueChange.emit(this.value() + 1);
  }

  dec(): void {
    if (this.canDec()) this.valueChange.emit(this.value() - 1);
  }

  constructor() {
    addIcons({ addOutline, removeOutline });
  }
}
